import {
  ActivitySpinnerSize, ActivitySpinnerWidget,
  FilteringList, ListItem, TabStyles
} from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { style } from 'typestyle';

import { interpolateString } from '~common/utilities/StringInterpolation';
import { replacements } from '~src/home/components/NoFavoritesAndRecentsChannel';

import { Formatters, Messages } from '../../codegen/Localize';
import { FormattedMatch } from '../../common/react/FormattedMatch';
import { getExplorableItemDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { HeaderStyles } from './HeaderStyles';

export interface FavoritesListProps {
  readonly id?: string;
  readonly items: IFavorite[];  // named 'items' to keep in sync with widgets props
  readonly favoritesRequestCompleted: boolean;
  readonly onSelect: (value: string) => void;
  readonly urlForFavorite: (favorite: IFavorite) => string;
}

export interface FavoritesListState {
  readonly filteredIndices: number[];
  readonly searchText: string;
}

const centerContent = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

export class FavoritesList extends React.PureComponent<FavoritesListProps, FavoritesListState> {

  private memoizedFavoriteListItems = defaultMemoize(createFavoritesListItems);

  public constructor(props: FavoritesListProps) {
    super(props);
    this.state = {filteredIndices: [], searchText: ''};
  }

  public render(): JSX.Element {
    let insideList: JSX.Element = undefined;
    let liveText: string = '';

    if (!this.props.favoritesRequestCompleted) {
      // show spinner until we have favorites
      liveText = Messages.favoritesFetchingItems();

      insideList = (
        <div className={HeaderStyles.FavoritesSpinner} >
          <ActivitySpinnerWidget
            spinnerSize={ActivitySpinnerSize.Small}
            testId="favorites-list-spinner" />
        </div>
      );

    } else {

      const listItems = this.memoizedFavoriteListItems(this.props.items.filter((_fav, index) => {
        return this.state.searchText.length === 0 || this.state.filteredIndices.includes(index);
      }), this.state.searchText, this.props.urlForFavorite);

      liveText = listItems.length === 0 ?
        Messages.favoritesShowingNoItems() :
        Messages.favoritesShowingItems({ TOTAL_COUNT_LOC: Formatters.formatCount(listItems.length), TOTAL_COUNT: listItems.length });

      /* eslint-disable jsx-a11y/no-autofocus */
      insideList = (
        <div
          className={HeaderStyles.FavoritesPanel}
          data-tb-test-id="favorites-menu-panel"
          style={{fontWeight: 'normal'}}
        >
          <FilteringList
            onSelect={this.props.onSelect}
            items={listItems}
            autoFocus={true}
            style={HeaderStyles.FavoritesList}
            onFilterChange={this.onFilterChange}
            searchText={this.state.searchText}
            searchPlaceholder={Messages.searchFavorites()}
            inputTestId={'favorites-menu-list-search'}
            listTestId={'favorites-menu-list'}
          />
          {this.bottomMessage(listItems.length === 0)}
        </div>);

    }

    return (
      <div id={this.props.id}>
        <div aria-live={'polite'} role={'status'} className={'sr-only'}>{liveText}</div>
        {insideList}
      </div>
    );
  }

  private bottomMessage = (noListItems: boolean): JSX.Element => {
    if (this.props.items.length === 0) {
      return (
        <div
          style={{ textAlign: 'center', marginBottom: TabStyles.Spacing.MediumSpace }}
          data-tb-test-id="tb-no-favorites"
        >
          <div className={centerContent}>
            {interpolateString(Messages.home_emptyFavoritesAndRecents(), replacements)}
          </div>
        </div>
      );
    } else if (this.state.searchText.length > 0 && noListItems) {
      return (
        <div
          style={{ textAlign: 'center', marginBottom: TabStyles.Spacing.MediumSpace }}
          data-tb-test-id="tb-no-results"
        >
          {Messages.noMatchingResults()}
        </div>
      );
    }

    return null;
  }

  private onFilterChange = (value: string) => {
    if (!this.props.favoritesRequestCompleted) {
      this.setState({searchText: value});
      return;
    }
    const filteredIndices = this.props.items.reduce<number[]>((currentIndices, favorite, index) => {
      if (value.length > 0 && favorite.objectName.toLowerCase().includes(value.toLowerCase())) {
        currentIndices.push(index);
      }
      return currentIndices;
    }, []);
    this.setState({filteredIndices, searchText: value});
  }
}

function createFavoriteListItem(favorite: IFavorite, searchText: string, urlForFavorite: (favorite: IFavorite) => string): JSX.Element {
  // This will be removed once this TFS item is done : TFSID 900897
  if (favorite.objectType.toString() === 'datarole') {
    favorite.objectType = 'dataRole';
  }

  const src = getExplorableItemDefinition(favorite.objectType as ExplorableItemType).listViewIconUrl;
  /* eslint-disable jsx-a11y/alt-text */
  return (
    <a
      href={urlForFavorite(favorite)}
      tabIndex={-1}
      className={HeaderStyles.FavoritesItemContainer}
      data-tb-test-id={`favorite-list-item-${favorite.objectType}-${favorite.objectId}`}
      title={favorite.objectName}
    >
      <img src={src} className={HeaderStyles.FavoritesIcon} />
      {<FormattedMatch style={HeaderStyles.FormattedMatch} searchTerm={searchText} text={favorite.objectName} matchPrefixesOnly={false}/>}
    </a>
  );

}

function createFavoritesListItems(favorites: ReadonlyArray<IFavorite>, searchText: string, urlForFavorite: (favorite: IFavorite) => string): ListItem[] {
  return favorites.map((favorite) => {
    return {
      value: getFavoriteKey(favorite),
      testId: `favorite-${favorite.objectType}-${favorite.objectId}`,
      content: createFavoriteListItem(favorite, searchText, urlForFavorite)
    };
  });
}

export function getFavoriteKey(favorite: IFavorite): string {
  return favorite.id;
}
